import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, NgForm } from '@angular/forms';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { Mask } from '@workflow/class/workflow.type';
import { InputStatus } from '@workflow/directive/input-mask/input-mask-processing.base';
import { maskInput } from '@workflow/directive/input-mask/mask.helper';
import { CustomFields } from '@workflow/ecp-workflow/dynamic-app/types/dynamic-custom-fields.type';
import { getValidationObject } from '@workflow/helper/validation-converter/validation-converter';

/** This component dyanmically generates membership eligibility questions. */
@BuildingBlock('generate-custom-fields')
@Component({
  selector: 'ecp-dynamic-custom-field',
  templateUrl: './dynamic-custom-field.component.html'
})
export class DynamicCustomFieldComponent extends BuildingBlockControlBaseComponent implements OnInit, AfterViewInit {
  /** Control Type Textbox */
  public textbox = 'textbox';
  /** Control Type Currency */
  public currency = 'currency';
  /** Mask type currency decimal */
  public currency_decimals = 'currency-decimals' as Mask;
  /** Mask type percent decimals */
  public percent_decimals = 'percent-decimals' as Mask;
  /** Mask type Int */
  public int = 'int' as Mask;
  /** Placeholder % */
  public placeholder_percent = '%';
  /** Placeholder $ */
  public placeholder_dollar = '$';
  /** Placeholder number */
  public placeholder_number = '';
  /** Control Type Integer */
  public integer = 'integer';
  /** Control Type Checkbox */
  public checkbox = 'checkbox';
  /** Control Type Precentage Precision */
  public percentageprecision = 'percentageprecision';
  /** Application Custom Field Declaration */
  public applicationCustomFields: CustomFields[];
  /** last state of mobile number input */
  private lastState: InputStatus;
  /** No questions found msg */
  public questions_not_found = '';

  public isSubmitClicked = false;

  /** Form */
  @ViewChild('customFieldForm') public customFieldForm: NgForm;

  public ngOnInit(): void {
    this.applicationCustomFields = [];
    this.getDynamicCustomFields();
    this.pageSubmitted$.subscribe((value) => {
      this.isSubmitClicked = value;
    });
  }

  public ngAfterViewInit(): void {
    if (this.customFieldForm) {
      /** Subscribe ngForm status change event */
      this.customFieldForm.statusChanges.subscribe((x) => {
        this.field.data.value = x.toString().toLowerCase() === 'invalid' ? false : true;
      });
    }
  }

  /** Get Dynamic Custom Field by calling API */
  private getDynamicCustomFields(): void {
    const hideAdditionalQuestionsFlag = this.field.config.payload.flagUpdate as string;
    const cachedCustomfields = this.l360Service.cachedCustomfields;
    if (cachedCustomfields?.length) {
      this.handleCustomFieldResponse(cachedCustomfields, hideAdditionalQuestionsFlag);
      return;
    }

    const l360Service = this.field.config.payload.l360Service as string;
    const params = (this.field.config.payload.params || {}) as { [key: string]: string };
    const requestParam = {};
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const element = params[key];
        requestParam[key] = this.statefulService.wrap(element).valueString;
      }
    }
    if (requestParam && this.applicationCustomFields.length === 0) {
      this.statefulService.wrap('mem:show-card-spinner-by-id').value = this.field.id;
      this.statefulService.wrap('mem:show-card-spinner').value = true;
      if (typeof this.l360Service[l360Service] != 'function') {
        return;
      }
      this.l360Service[l360Service](requestParam).subscribe(
        (response) => {
          if (response.isSuccess) {
            this.handleCustomFieldResponse(response.data.applicationCustomFields, hideAdditionalQuestionsFlag);
          } else {
            this.field.data.value = true;
            this.statefulService.wrap(hideAdditionalQuestionsFlag).value = true;
          }
          setTimeout(() => {
            this.field.data.value = this.getInvalidList()?.length <= 0;
          }, 100);
        },
        () => {
          this.field.data.value = true;
          this.questions_not_found = 'No question(s) available to display.';
          this.statefulService.wrap('mem:show-card-spinner-by-id').value = '';
          this.statefulService.wrap('mem:show-card-spinner').value = false;
          this.statefulService.wrap(hideAdditionalQuestionsFlag).value = true;
        }
      );
    }
  }

  private handleCustomFieldResponse(applicationCustomFields: CustomFields[], hideAdditionalQuestionsFlag: string) {
    this.applicationCustomFields = applicationCustomFields;
    if (this.applicationCustomFields.length === 0) {
      this.field.data.value = true;
      this.statefulService.wrap(hideAdditionalQuestionsFlag).value = true;
    } else {
      this.applicationCustomFields.forEach((field, index) => {
        field.specificControlType = (field.specificControlType || '').toLowerCase();
        if (!this.hasTextField(field.specificControlType)) {
          field.fieldValue = JSON.parse(field.fieldValue);
        }
        this.updateFieldLabelData(index, field.fieldLabel);
      });
      this.statefulService.wrap(hideAdditionalQuestionsFlag).value = false;
    }
    this.statefulService.wrap('mem:show-card-spinner-by-id').value = '';
    this.statefulService.wrap('mem:show-card-spinner').value = false;
    // Have a slight race condition here between the GET OLA/OAO bundle & GetCustomFields API calls with GET bundle finishing first
    // Checking for bundle value, if not yet populated making a GET call to get the latest
    this.validateBundle();
  }

  public getInvalidList(): string[] {
    return Object.keys(this.customFieldForm.form.controls).filter((control: string) => {
      const typedControl: AbstractControl = this.customFieldForm.form.controls[control];
      return typedControl.status.toString().toLowerCase() === 'invalid';
    });
  }

  /**
   * method to validate the bundle and make a GET call if needed
   */
  public validateBundle() {
    const isOAO = this.field.config.payload.l360Service.toString().includes('OAO');
    const olaCustomFieldPropertyPathIDBundleValue = this.statefulService.wrap(
      'ola:olaApplicationInformation/customField/applicationCustomFields/0/propertyPathID'
    ).valueString;
    const oaoCustomFieldPropertyPathIDBundleValue = this.statefulService.wrap(
      'api:oaoMemberInformation/customField/applicationCustomFields/0/propertyPathID'
    ).valueString;

    if (isOAO) {
      if (!oaoCustomFieldPropertyPathIDBundleValue) {
        // Using one of the common ID's from the bundle to make a GET call
        this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').load(() => {});
      }
    } else {
      if (!olaCustomFieldPropertyPathIDBundleValue) {
        // Using one of the common ID's from the bundle to make a GET call
        this.statefulService.wrap('ola:borrowerInfo/firstName').load(() => {});
      }
    }
  }

  public getMaxLength(field) {
    if ((field.specificControlType || '').toLowerCase() === this.textbox) {
      return field.maxLength;
    }
    return null;
  }

  /** Checking Text Field or Not */
  public hasTextField(specificControlType: string): boolean {
    const fieldType = specificControlType.toLowerCase();
    if (fieldType === this.textbox || fieldType === this.currency || fieldType === this.integer || fieldType === this.percentageprecision) {
      return true;
    }
    return false;
  }

  /** methods helps to process mask */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public processMask(inputElement: any, field: CustomFields, fieldIndex: number, setSelection = true): void {
    let maskType: Mask;

    switch ((field.specificControlType || '').toLowerCase()) {
      case this.currency:
        maskType = this.currency_decimals;
        break;
      case this.percentageprecision:
        maskType = this.percent_decimals;
        break;
      case this.integer:
        maskType = this.int;
        break;
      default:
    }

    if (maskType) {
      const mask = maskInput(maskType, this.readState(inputElement), this.lastState, getValidationObject(this.field));
      if (mask.changed) {
        inputElement.target.value = mask.processed.value;
        if (setSelection && inputElement.target.value) {
          inputElement.target.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
        }
        inputElement.target.value = mask.processed.value;
      }
      this.lastState = this.readState(inputElement);
    }

    this.updateBundleData(inputElement.target.value, fieldIndex, field?.fieldLabel);
  }

  /** Read inputelement state */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readState(inputElement: any): InputStatus {
    const domElement = inputElement.target;
    return {
      focused: this.focused,
      selection: [domElement.selectionStart, domElement.selectionEnd],
      value: inputElement.target.value
    };
  }

  /**
   * Method to update bundle data: field value and field label if null
   * @param val - field value
   * @param index - field index
   * @param fieldLabelValue - field label value
   */
  public updateBundleData(val: string, index: number, fieldLabelValue: string): void {
    const fieldValueStoragePath = this.field.config.payload.storageId as string;
    this.statefulService.wrap(fieldValueStoragePath, index).value = val;
    this.updateFieldLabelData(index, fieldLabelValue);
  }

  /**
   * Method to update field label bundle data
   * @param fieldLabelBundleValue - current field label bundle value
   * @param fieldLabelStoragePath - storage path of field label
   * @param index - field index
   * @param fieldLabelValue - value to be assigned
   */
  updateFieldLabelData(index: number, fieldLabelValue: string): void {
    const isOAO = this.field.config.storageId.toString().includes('oao');
    const fieldLabelStoragePath = isOAO
      ? 'api:oaoMemberInformation/customField/applicationCustomFields/$/fieldLabel'
      : 'ola:olaApplicationInformation/customField/applicationCustomFields/$/fieldLabel';
    const fieldLabelBundleValue = this.statefulService.wrap(fieldLabelStoragePath, index).valueString;
    if (!fieldLabelBundleValue) {
      // Since we have initialized 20 items into the bundle during creation and
      // there's a possibility of race condition with bundle data not yet being updated with the API response
      // Binding the Field label or Question Text value as well when it's null or not yet updated
      this.statefulService.wrap(fieldLabelStoragePath, index).value = fieldLabelValue;
    }
  }

  public getPlaceholderText(controlType: string): string {
    let placeholderText = '';
    switch ((controlType || '').toLowerCase()) {
      case this.currency:
        placeholderText = this.placeholder_dollar;
        break;
      case this.percentageprecision:
        placeholderText = this.placeholder_percent;
        break;
      case this.integer:
        placeholderText = this.placeholder_number;
        break;
      default:
    }
    return placeholderText;
  }
}
